import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CreditTopUpCard from './CreditTopUpCard';
import { providerApi } from '../../services/api';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch (_) { return iso; }
}

function fmtAmount(cents, currency) {
  if (cents == null) return '—';
  const symbol = currency === 'ILS' ? '₪' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : (currency || '');
  return `${symbol}${(cents / 100).toFixed(0)}`;
}

const STATUS_CHIP = {
  ACTIVE:          { color: 'success', variant: 'filled' },
  PAUSED:          { color: 'info',    variant: 'outlined' },
  DRAFT:           { color: 'warning', variant: 'outlined' },
  COMPLETED:       { color: 'default', variant: 'outlined' },
  REJECTED:        { color: 'error',   variant: 'outlined' },
  PENDING_PAYMENT: { color: 'warning', variant: 'outlined' },
  PENDING_REVIEW:  { color: 'warning', variant: 'outlined' },
};

function StatusChip({ status, t }) {
  const cfg = STATUS_CHIP[status] || { color: 'default', variant: 'outlined' };
  const label = t(`promotions.status${status.charAt(0) + status.slice(1).toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`);
  return <Chip label={label} color={cfg.color} variant={cfg.variant} size="small" />;
}

function PromotionDetails({ row, t }) {
  const [spendOpen, setSpendOpen] = useState(false);
  const countries = row.targetCountries?.map(c => c.name).filter(Boolean) ?? [];
  const cities = row.targetCities?.map(c => c.name).filter(Boolean) ?? [];
  const breeds = row.targetDogBreeds ?? [];
  const sizes = row.targetDogSizes ?? [];
  const energies = row.targetDogEnergies ?? [];
  const gardenCount = row.targetGardenIds?.length ?? 0;
  const groupCount = row.targetGroupIds?.length ?? 0;
  const hasAudience = countries.length > 0 || cities.length > 0 || breeds.length > 0 ||
    sizes.length > 0 || energies.length > 0 || row.targetDogAgeMin != null ||
    row.targetDogAgeMax != null || gardenCount > 0 || groupCount > 0;

  const period = row.startAt && row.endAt
    ? `${fmtDate(row.startAt)} → ${fmtDate(row.endAt)}`
    : row.startAt
    ? `${t('promotions.detailFrom')} ${fmtDate(row.startAt)}`
    : t('promotions.detailNotStarted');

  const AudienceChips = ({ label, items }) => items.length === 0 ? null : (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
        {items.map(item => <Chip key={item} label={item} size="small" variant="outlined" />)}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
      {/* Campaign overview */}
      <Typography variant="caption" color="text.secondary" fontWeight={700}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
        {t('promotions.detailCampaignTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">{t('promotions.detailDuration')}</Typography>
          <Typography variant="body2" fontWeight={600}>
            {row.durationDays ? `${row.durationDays} ${t('promotions.detailDays')}` : '—'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">{t('promotions.detailDailyBudget')}</Typography>
          <Typography variant="body2" fontWeight={600}>{fmtAmount(row.dailyBudgetCents, row.currency)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">{t('promotions.detailPeriod')}</Typography>
          <Typography variant="body2" fontWeight={600}>{period}</Typography>
        </Box>
        {row.cta && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">{t('promotions.detailCta')}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {row.cta}
              {row.ctaUrl && (
                <> · <a href={row.ctaUrl} target="_blank" rel="noreferrer noopener"
                  style={{ fontSize: 'inherit' }}>{row.ctaUrl}</a></>
              )}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Daily spend history */}
      {row.dailySpends?.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setSpendOpen(o => !o)}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, flexGrow: 1 }}>
              {t('promotions.detailSpendHistory')}
            </Typography>
            <IconButton size="small" sx={{ p: 0.25 }}>
              {spendOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={spendOpen} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1, mb: 1 }}>
              {row.dailySpends.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{fmtDate(s.date)}</Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    -{fmtAmount(s.amountCents, row.currency)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Target audience */}
      <Typography variant="caption" color="text.secondary" fontWeight={700}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
        {t('promotions.detailAudienceTitle')}
      </Typography>
      {!hasAudience ? (
        <Typography variant="body2" color="text.secondary">{t('promotions.detailAnyAudience')}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <AudienceChips label={t('promotions.detailCountries')} items={countries} />
          <AudienceChips label={t('promotions.detailCities')} items={cities} />
          <AudienceChips label={t('promotions.detailBreeds')} items={breeds} />
          <AudienceChips label={t('promotions.detailSizes')} items={sizes} />
          <AudienceChips label={t('promotions.detailEnergies')} items={energies} />
          {(row.targetDogAgeMin != null || row.targetDogAgeMax != null) && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('promotions.detailAgeRange')}</Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.targetDogAgeMin ?? 0} – {row.targetDogAgeMax ?? '∞'} {t('promotions.detailMonths')}
              </Typography>
            </Box>
          )}
          {gardenCount > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('promotions.detailGardens')}</Typography>
              <Typography variant="body2" fontWeight={600}>{gardenCount} {t('promotions.detailGardens')}</Typography>
            </Box>
          )}
          {groupCount > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('promotions.detailGroups')}</Typography>
              <Typography variant="body2" fontWeight={600}>{groupCount} {t('promotions.detailGroups')}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function PromotedPostsSection({ refreshSignal, onNeedTopUp }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const toggleRow = (id) => setExpandedRows(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    providerApi.getMyPromotions()
      .then(data => setRows(data || []))
      .catch(() => setError(t('promotions.postsLoadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const handleAction = async (row, action) => {
    setActionLoading(row.id);
    setActionError('');
    try {
      let updated;
      if (action === 'activate') updated = await providerApi.activatePromotion(row.postId);
      else if (action === 'pause')    updated = await providerApi.pausePromotion(row.postId);
      else if (action === 'resume')   updated = await providerApi.resumePromotion(row.postId);
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...updated } : r));
    } catch (err) {
      if (err?.response?.status === 409 && action === 'activate') {
        setActionError(t('promotions.insufficientBalance'));
        onNeedTopUp?.();
      } else {
        setActionError(t('promotions.actionFailed'));
      }
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        {t('promotions.postsTitle')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('promotions.postColPost')}</TableCell>
              <TableCell>{t('promotions.postColDate')}</TableCell>
              <TableCell align="right">{t('promotions.postColLikes')}</TableCell>
              <TableCell align="right">{t('promotions.postColImpressions')}</TableCell>
              <TableCell align="right">{t('promotions.postColClicks')}</TableCell>
              <TableCell align="right">{t('promotions.postColBudget')}</TableCell>
              <TableCell align="right">{t('promotions.postColSpent')}</TableCell>
              <TableCell>{t('promotions.postColStatus')}</TableCell>
              <TableCell>{t('promotions.postColDetails')}</TableCell>
              <TableCell>{t('promotions.postColActions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {[0,1,2,3,4,5,6,7,8,9].map(c => (
                    <TableCell key={c}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  {t('promotions.postsEmpty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => {
                const isActioning = actionLoading === row.id;
                const isExpanded = expandedRows.has(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover sx={{ '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}>
                      <TableCell>
                        {row.postThumbnailUrl ? (
                          <Avatar
                            src={row.postThumbnailUrl}
                            variant="rounded"
                            sx={{ width: 48, height: 48 }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'grey.200' }}>
                            <ImageIcon fontSize="small" sx={{ color: 'grey.500' }} />
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.createdAt)}</TableCell>
                      <TableCell align="right">{row.postLikesCount ?? 0}</TableCell>
                      <TableCell align="right">{row.impressions ?? 0}</TableCell>
                      <TableCell align="right">{row.clicks ?? 0}</TableCell>
                      <TableCell align="right">{fmtAmount(row.totalBudgetCents, row.currency)}</TableCell>
                      <TableCell align="right">{fmtAmount(row.amountSpentCents, row.currency)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <StatusChip status={row.status} t={t} />
                          {row.status === 'DRAFT' && (
                            <Typography variant="caption" color="text.secondary">
                              {t('promotions.draftHint')}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell padding="none" align="center">
                        <IconButton size="small" onClick={() => toggleRow(row.id)}
                          aria-label={isExpanded ? t('promotions.detailCollapse') : t('promotions.detailExpand')}>
                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {row.status === 'DRAFT' && (
                          <Tooltip title={t('promotions.actionLaunch')}>
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={isActioning ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                disabled={isActioning}
                                onClick={() => handleAction(row, 'activate')}
                              >
                                {t('promotions.actionLaunch')}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {row.status === 'ACTIVE' && (
                          <Tooltip title={t('promotions.actionPause')}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={isActioning ? <CircularProgress size={14} color="inherit" /> : <PauseIcon />}
                                disabled={isActioning}
                                onClick={() => handleAction(row, 'pause')}
                              >
                                {t('promotions.actionPause')}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {row.status === 'PAUSED' && (
                          <Tooltip title={t('promotions.actionResume')}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={isActioning ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                disabled={isActioning}
                                onClick={() => handleAction(row, 'resume')}
                              >
                                {t('promotions.actionResume')}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ p: 0 }} colSpan={10}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <PromotionDetails row={row} t={t} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function PaymentHistoryTable({ refreshSignal }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    providerApi.getTopUpHistory()
      .then(data => setRows(data || []))
      .catch(() => setError(t('promotions.historyLoadFailed')))
      .finally(() => setLoading(false));
  }, [t, refreshSignal]);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        {t('promotions.historyTitle')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('promotions.historyColDate')}</TableCell>
              <TableCell align="right">{t('promotions.historyColAmount')}</TableCell>
              <TableCell>{t('promotions.historyColOrderId')}</TableCell>
              <TableCell>{t('promotions.historyColCaptureId')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {[0, 1, 2, 3].map(c => (
                    <TableCell key={c}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  {t('promotions.historyEmpty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.paidAt || row.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={fmtAmount(row.amountCents, row.currency)}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                    {row.paypalOrderId || '—'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                    {row.paypalCaptureId || '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function PromotionsPage() {
  const { t } = useTranslation();
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [postsRefresh, setPostsRefresh] = useState(0);
  const creditCardRef = React.useRef(null);

  const handleCaptureSuccess = () => {
    setHistoryRefresh(r => r + 1);
    setPostsRefresh(r => r + 1);
  };

  const scrollToTopUp = () => {
    creditCardRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {t('nav.promotions')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('promotions.subtitle')}
      </Typography>

      <div ref={creditCardRef}>
        <CreditTopUpCard onCaptureSuccess={handleCaptureSuccess} />
      </div>

      <Box sx={{ mt: 4 }}>
        <PromotedPostsSection
          refreshSignal={postsRefresh}
          onNeedTopUp={scrollToTopUp}
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        <PaymentHistoryTable refreshSignal={historyRefresh} />
      </Box>
    </Box>
  );
}
